import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBreadcrumbs } from '../../contexts/BreadcrumbContext';
import { getResumeVersion, getResumeVersions, Resume, ResumeVersionSummary } from '../../services/resume';
import { ResumeCompare } from '../../components/Resume/ResumeCompare';
import './ResumeHistory.css';

export function ResumeHistory() {
  const navigate = useNavigate();
  const { setPageCrumbs } = useBreadcrumbs();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [versions, setVersions] = useState<ResumeVersionSummary[]>([]);
  const [viewingVersion, setViewingVersion] = useState<number | null>(null);
  const [selectedResume, setSelectedResume] = useState<Resume | null>(null);
  const [compareMode, setCompareMode] = useState(false);
  const [selectedVersionA, setSelectedVersionA] = useState<Resume | null>(null);
  const [selectedVersionB, setSelectedVersionB] = useState<Resume | null>(null);

  useEffect(() => {
    setPageCrumbs([{ label: 'Resume History' }]);
  }, [setPageCrumbs]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const history = await getResumeVersions();
        setVersions(history);
      } catch (err: any) {
        console.error('Failed to load resume history:', err);
        setError(err?.message || 'Failed to load resume history');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleViewVersion = async (version: number) => {
    try {
      setViewingVersion(version);
      setError('');
      const snapshot = await getResumeVersion(version);
      setSelectedResume(snapshot);
    } catch (err: any) {
      console.error('Failed to load resume version:', err);
      setError(err?.message || 'Failed to load selected version');
    } finally {
      setViewingVersion(null);
    }
  };

  const handleSelectForComparison = async (version: number) => {
    try {
      const snapshot = await getResumeVersion(version);
      if (!selectedVersionA) {
        setSelectedVersionA(snapshot);
      } else if (!selectedVersionB) {
        setSelectedVersionB(snapshot);
      } else {
        // Replace the first selection if both are already chosen
        setSelectedVersionA(snapshot);
        setSelectedVersionB(null);
      }
    } catch (err: any) {
      console.error('Failed to load resume version:', err);
      setError(err?.message || 'Failed to load version for comparison');
    }
  };

  const handleStartComparison = () => {
    if (selectedVersionA && selectedVersionB) {
      setCompareMode(true);
    }
  };

  const handleCancelComparison = () => {
    setCompareMode(false);
    setSelectedVersionA(null);
    setSelectedVersionB(null);
  };

  const formatDate = (value: string | null) => {
    if (!value) return 'Unknown date';
    return new Date(value).toLocaleDateString();
  };

  return (
    <div className="resumeHistoryPage">
      <div className="resumeHistoryHeader">
        <h2 className="resumeHistoryTitle">Resume History</h2>
        <button
          className="resumeHistoryButton"
          onClick={() => navigate('/resume')}
        >
          Back to Resume
        </button>
      </div>

      {loading ? (
        <div className="resumeHistoryCard">
          <p className="resumeHistoryMuted">Loading versions...</p>
        </div>
      ) : error ? (
        <div className="resumeHistoryCard">
          <p className="resumeHistoryError">{error}</p>
        </div>
      ) : versions.length === 0 ? (
        <div className="resumeHistoryCard">
          <p className="resumeHistoryMuted">No resume versions found yet.</p>
        </div>
      ) : (
        <div className="resumeHistoryGrid">
          <div className="resumeHistoryCard">
            <h3 className="resumeHistorySectionTitle">Saved Versions</h3>
            <div className="resumeHistoryList">
              {versions.map((item) => (
                <div key={item.id} className="resumeHistoryListItem">
                  <div>
                    <p className="resumeHistoryVersion">Version {item.version}</p>
                    <p className="resumeHistoryMuted">{formatDate(item.updatedAt || item.createdAt)}</p>
                  </div>
                  <div className="resumeHistoryListItemActions">
                    <button
                      className="resumeHistoryButton"
                      onClick={() => handleViewVersion(item.version)}
                      disabled={viewingVersion === item.version}
                    >
                      {viewingVersion === item.version ? 'Loading...' : 'View'}
                    </button>
                    <button
                      className={`resumeHistoryButton ${
                        selectedVersionA?.version === item.version || selectedVersionB?.version === item.version
                          ? 'selected'
                          : ''
                      }`}
                      onClick={() => handleSelectForComparison(item.version)}
                      title="Select for comparison (choose two versions)"
                    >
                      {selectedVersionA?.version === item.version || selectedVersionB?.version === item.version
                        ? 'Selected'
                        : 'Compare'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="resumeHistoryCard">
            <h3 className="resumeHistorySectionTitle">Snapshot Preview (Read-only)</h3>
            {!selectedResume ? (
              <p className="resumeHistoryMuted">Select a version to view its snapshot.</p>
            ) : (
              <div className="resumeHistorySnapshot">
                <p className="resumeHistoryVersion">Version {selectedResume.version}</p>
                <p className="resumeHistoryMuted">Updated {formatDate(selectedResume.updatedAt)}</p>

                <div className="resumeHistoryBlock">
                  <h4>Personal Information</h4>
                  <p>{selectedResume.data.personalInfo.name || '—'}</p>
                  <p>{selectedResume.data.personalInfo.email || '—'}</p>
                  {selectedResume.data.personalInfo.location && <p>{selectedResume.data.personalInfo.location}</p>}
                </div>

                <div className="resumeHistoryBlock">
                  <h4>Summary</h4>
                  <p>{selectedResume.data.summary || 'No summary'}</p>
                </div>

                <div className="resumeHistoryBlock">
                  <h4>Experience</h4>
                  {(selectedResume.data.experience || []).length === 0 ? (
                    <p>No experience entries</p>
                  ) : (
                    <ul>
                      {(selectedResume.data.experience || []).map((exp, index) => (
                        <li key={`${exp.company}-${exp.position}-${index}`}>
                          {exp.position || 'Role'} @ {exp.company || 'Company'}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {(selectedVersionA || selectedVersionB) && (
        <div className="resumeHistoryComparisonBar">
          <div className="resumeHistoryComparisonBarContent">
            <span className="resumeHistoryComparisonBarLabel">
              {selectedVersionA && selectedVersionB
                ? `Comparing: Version ${selectedVersionA.version} ↔ Version ${selectedVersionB.version}`
                : `Select ${selectedVersionA ? '1 more version' : 'a version'} to compare`}
            </span>
            <div className="resumeHistoryComparisonBarActions">
              {selectedVersionA && selectedVersionB && (
                <button
                  className="resumeHistoryButton"
                  onClick={handleStartComparison}
                >
                  View Comparison
                </button>
              )}
              <button
                className="resumeHistoryButtonSecondary"
                onClick={handleCancelComparison}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {compareMode && selectedVersionA && selectedVersionB && (
        <ResumeCompare
          baseResume={selectedVersionA}
          comparedResume={selectedVersionB}
          onClose={handleCancelComparison}
        />
      )}
    </div>
  );
}
